/**
 * Direct access to Google Drive without CORS proxy
 * Works only for public folders and files
 */

const GOOGLE_DRIVE_FOLDER_ID = '1g_jpa1rkycfdaq-NizLUZkFQZ-tDaagK';

/**
 * Method 1: Via embedded Google Drive view
 * Creates iframe with folder content
 */
export const createGoogleDriveEmbed = containerId => {
  const container = document.getElementById(containerId);
  if (!container) return;

  const iframe = document.createElement('iframe');
  iframe.src = `https://drive.google.com/embeddedfolderview?id=${GOOGLE_DRIVE_FOLDER_ID}#grid`;
  iframe.style.width = '100%';
  iframe.style.height = '600px';
  iframe.style.border = 'none';
  iframe.title = 'Google Drive Resources';

  container.appendChild(iframe);
};

/**
 * Method 2: Via Google Drive Viewer API
 * Gets file list via public API (limited functionality)
 */
export const getGoogleDrivePublicFiles = async () => {
  try {
    // For public folders, direct access can be used
    const publicUrl = `https://drive.google.com/drive/folders/${GOOGLE_DRIVE_FOLDER_ID}`;

    // Return structured information for known files
    return [
      {
        id: 'public_folder_1',
        name: 'AllergiEnd Documents',
        type: 'folder',
        description: 'Main folder with documentation',
        url: publicUrl,
        isPublic: true,
      },
    ];
  } catch (error) {
    console.error('Error accessing public folder:', error);
    return [];
  }
};

/**
 * Method 3: Via Google Apps Script web application
 * Create a web application in Google Apps Script to get file list
 */
export const getFilesViaGoogleAppsScript = async scriptUrl => {
  try {
    const response = await fetch(scriptUrl);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.files || [];
  } catch (error) {
    console.error('Error loading via Google Apps Script:', error);
    return [];
  }
};

/**
 * Method 4: Static file list
 * Manually maintained list of important resources
 */
export const getStaticResourcesList = () => {
  return [
    {
      id: 'manual_1',
      name: 'AllergiEnd Manual',
      type: 'file',
      description: 'Complete guide to using AllergiEnd',
      url: `https://drive.google.com/drive/folders/${GOOGLE_DRIVE_FOLDER_ID}`,
      directLink: 'https://drive.google.com/file/d/YOUR_FILE_ID/view',
      mimeType: 'application/pdf',
      category: 'documentation',
    },
    {
      id: 'manual_2',
      name: 'Video Instructions',
      type: 'folder',
      description: 'Video materials for training',
      url: `https://drive.google.com/drive/folders/${GOOGLE_DRIVE_FOLDER_ID}`,
      itemCount: 5,
      category: 'training',
    },
    {
      id: 'manual_3',
      name: 'Patient Materials',
      type: 'folder',
      description: 'Information brochures and leaflets',
      url: `https://drive.google.com/drive/folders/${GOOGLE_DRIVE_FOLDER_ID}`,
      itemCount: 12,
      category: 'patient_materials',
    },
    {
      id: 'manual_4',
      name: 'Clinical Research',
      type: 'folder',
      description: 'Research results and statistics',
      url: `https://drive.google.com/drive/folders/${GOOGLE_DRIVE_FOLDER_ID}`,
      itemCount: 8,
      category: 'research',
    },
  ];
};

/**
 * Main function for getting resources
 * Tries different methods in sequence
 */
export const getGoogleDriveResources = async (options = {}) => {
  const { useAPI = true, useStatic = true, apiKey = null, appsScriptUrl = null } = options;

  // Method 1: Google Drive API (if API key available)
  if (useAPI && apiKey) {
    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q='${GOOGLE_DRIVE_FOLDER_ID}'+in+parents&key=${apiKey}&fields=files(id,name,mimeType,size,modifiedTime,description,webViewLink,webContentLink)`
      );

      if (response.ok) {
        const data = await response.json();
        return data.files.map(file => ({
          id: file.id,
          name: file.name,
          type: file.mimeType === 'application/vnd.google-apps.folder' ? 'folder' : 'file',
          mimeType: file.mimeType,
          description: file.description || '',
          url: file.webViewLink,
          downloadUrl: file.webContentLink,
          lastModified: file.modifiedTime,
          size: file.size,
          source: 'google_api',
        }));
      }
    } catch (error) {
      console.warn('Google Drive API unavailable:', error.message);
    }
  }

  // Method 2: Google Apps Script (if configured)
  if (appsScriptUrl) {
    try {
      const files = await getFilesViaGoogleAppsScript(appsScriptUrl);
      if (files.length > 0) {
        return files.map(file => ({ ...file, source: 'apps_script' }));
      }
    } catch (error) {
      console.warn('Google Apps Script unavailable:', error.message);
    }
  }

  // Method 3: Static list (always works)
  if (useStatic) {
    console.log('Using static resource list');
    return getStaticResourcesList().map(item => ({
      ...item,
      source: 'static',
      lastModified: new Date().toISOString(),
    }));
  }

  return [];
};
